import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, EmptyState, Skeleton } from '@mailflow/ui';
import { Link } from '@tanstack/react-router';
import {
  actionStreamQuery,
  withoutNextActionQuery,
  useCompleteTask,
  useCreateTask,
  useLogActivity,
  contactThreadQuery,
  useSendContactEmail,
  type ActionStreamItem,
  type ContactWithoutNextAction,
} from '../lib/crm.js';
import { contactQuery } from '../lib/contacts.js';
import { useDebounced } from '../lib/useDebounced.js';
import { ContactTimeline, AddContactNoteForm } from './ContactDetailPage.js';

/**
 * Der Action Stream — die Tagesplanung des Vertriebs.
 *
 * Drei Festlegungen tragen die Ansicht:
 *
 *   1. Buendelung nach Dringlichkeit. Ueberfaellig zuerst, dann heute, dann
 *      der Rest. Eine flache Liste von hundert Aufgaben sagt nicht, ob man im
 *      Rueckstand ist.
 *   2. Der Gegenzaehler steht gleichberechtigt daneben, nicht als Warnbalken
 *      darueber. Ein leerer Stream sieht nach Feierabend aus, solange man
 *      nicht sieht, dass 300 Kontakte gar keinen naechsten Schritt haben.
 *   3. Die Buendel sind zugleich Filter. Wer den Rueckstand abarbeiten will,
 *      klickt auf „Überfällig" und sieht nur noch den Rueckstand — statt an
 *      allem anderen vorbeizuscrollen.
 *
 * Auf schmalem Schirm ersetzt die Akte den Stream, mit einem Rueckweg oben.
 * Zwei Spalten waeren dort zwei unlesbare Streifen.
 */

type Bucket = 'ueberfaellig' | 'heute' | 'morgen' | 'woche' | 'spaeter' | 'ohne';

const BUCKET_LABELS: Record<Bucket, string> = {
  ueberfaellig: 'Überfällig',
  heute: 'Heute',
  morgen: 'Morgen',
  woche: 'Diese Woche',
  spaeter: 'Später',
  ohne: 'Ohne Termin',
};

const BUCKET_ORDER: Bucket[] = ['ueberfaellig', 'heute', 'morgen', 'woche', 'spaeter', 'ohne'];

/**
 * Ordnet eine Faelligkeit einem Buendel zu.
 *
 * Gerechnet wird in Tagesgrenzen, nicht in 24-Stunden-Fenstern: eine Aufgabe,
 * die heute um 09:00 faellig war, gehoert um 14:00 unter „Überfällig" und
 * nicht unter „Heute" — sonst faellt der Rueckstand nicht auf.
 */
export function bucketFor(dueAt: string | null, now = new Date()): Bucket {
  if (!dueAt) return 'ohne';

  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return 'ohne';

  const tagesbeginn = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const tage = Math.round((tagesbeginn(due) - tagesbeginn(now)) / 86_400_000);

  if (tage < 0) return 'ueberfaellig';
  if (tage === 0) return due.getTime() < now.getTime() ? 'ueberfaellig' : 'heute';
  if (tage === 1) return 'morgen';
  if (tage <= 7) return 'woche';
  return 'spaeter';
}

const relativ = new Intl.RelativeTimeFormat('de-CH', { numeric: 'auto' });

/**
 * „vor 3 Tagen" statt „12.08.2026, 09:00".
 *
 * Beim Abarbeiten zaehlt der Abstand, nicht das Datum: wie lange etwas schon
 * liegt, liest sich aus einem Datum erst nach einer Kopfrechnung ab.
 */
function faelligkeitText(dueAt: string | null, now = new Date()): string {
  if (!dueAt) return 'Kein Termin';
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return 'Kein Termin';

  const minuten = Math.round((due.getTime() - now.getTime()) / 60_000);
  if (Math.abs(minuten) < 60) return relativ.format(minuten, 'minute');

  const stunden = Math.round(minuten / 60);
  if (Math.abs(stunden) < 24) return relativ.format(stunden, 'hour');

  const tage = Math.round(stunden / 24);
  if (Math.abs(tage) < 31) return relativ.format(tage, 'day');

  return due.toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ActionStreamPage() {
  const { data, isLoading } = useQuery(actionStreamQuery);
  const liegenGeblieben = useQuery(withoutNextActionQuery);

  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [detailOffen, setDetailOffen] = useState(false);
  const [promptNextActionId, setPromptNextActionId] = useState<string | null>(null);
  const [logForContactId, setLogForContactId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Bucket | 'alle' | 'ohne-aktion'>('alle');
  const [suche, setSuche] = useState('');
  const entprellt = useDebounced(suche, 150);

  const completeTask = useCompleteTask();

  const items = useMemo(() => data?.items ?? [], [data]);
  const ohneAktion = liegenGeblieben.data?.total ?? 0;

  /*
   * Solange niemand etwas angeklickt hat, steht der oberste Eintrag rechts.
   *
   * Abgeleitet statt im Effekt gesetzt: ein setState im useEffect loest einen
   * zweiten Durchlauf aus, und beim ersten blitzt „Wähle einen Kontakt" auf,
   * obwohl schon einer feststeht.
   */
  const activeContactId = selectedContactId ?? items[0]?.contact.id ?? null;

  const gefiltert = useMemo(() => {
    const begriff = entprellt.trim().toLowerCase();
    if (!begriff) return items;
    return items.filter((item) =>
      [
        item.contact.firstName,
        item.contact.lastName,
        item.contact.email,
        item.contact.companyName,
        item.task.title,
      ]
        .filter(Boolean)
        .some((feld) => feld!.toLowerCase().includes(begriff)),
    );
  }, [items, entprellt]);

  const gruppen = useMemo(() => {
    const karte = new Map<Bucket, ActionStreamItem[]>();
    for (const item of gefiltert) {
      const bucket = bucketFor(item.task.dueAt);
      const liste = karte.get(bucket) ?? [];
      liste.push(item);
      karte.set(bucket, liste);
    }
    return karte;
  }, [gefiltert]);

  const ueberfaellig = gruppen.get('ueberfaellig')?.length ?? 0;
  const heute = gruppen.get('heute')?.length ?? 0;

  const sichtbareBuendel =
    filter === 'alle' || filter === 'ohne-aktion'
      ? BUCKET_ORDER.filter((bucket) => (gruppen.get(bucket)?.length ?? 0) > 0)
      : BUCKET_ORDER.filter((bucket) => bucket === filter && (gruppen.get(bucket)?.length ?? 0) > 0);

  async function handleComplete(taskId: string, contactId: string, e: React.MouseEvent) {
    e.stopPropagation();
    await completeTask.mutateAsync(taskId);
    setPromptNextActionId(contactId);
  }

  function waehle(contactId: string) {
    setSelectedContactId(contactId);
    setDetailOffen(true);
  }

  if (isLoading) {
    return (
      <div className="stack" data-gap="4">
        <h1 className="page-header__title">Action Stream</h1>
        <Skeleton lines={5} shape="row" label="Action Stream wird geladen" />
      </div>
    );
  }

  return (
    <div className="stream" data-detail={detailOffen ? 'open' : undefined}>
      {/* --- LINKS: der Stream --------------------------------------------- */}
      <section className="stream__col" aria-label="Offene Aufgaben">
        <header className="stream__head">
          <div>
            <h1 className="page-header__title">Action Stream</h1>
            <p className="page-header__description">
              {ueberfaellig > 0
                ? `${ueberfaellig} überfällig, ${heute} heute fällig — der Rückstand zuerst.`
                : heute > 0
                  ? `Nichts überfällig. ${heute} Aufgaben sind heute fällig.`
                  : 'Nichts überfällig, nichts für heute.'}
            </p>
          </div>
        </header>

        <div className="stream__toolbar">
          <div className="search-field">
            <span className="search-field__icon" aria-hidden="true">
              ⌕
            </span>
            <input
              type="search"
              className="input search-field__input"
              value={suche}
              placeholder="Name, Firma, E-Mail oder Aufgabe …"
              aria-label="Aufgaben durchsuchen"
              onChange={(event) => setSuche(event.target.value)}
            />
            {suche && (
              <button
                type="button"
                className="search-field__clear"
                onClick={() => setSuche('')}
                aria-label="Suche zurücksetzen"
              >
                ×
              </button>
            )}
          </div>

          {/*
            Die Buendel sind zugleich der Filter. Zwei getrennte Bedienelemente
            — eine Uebersicht oben und eine Auswahlliste daneben — zeigten
            dieselbe Information doppelt.
          */}
          <div className="chip-row" role="group" aria-label="Nach Dringlichkeit filtern">
            <button
              type="button"
              className="chip"
              aria-pressed={filter === 'alle'}
              onClick={() => setFilter('alle')}
            >
              Alle
              <span className="chip__count" data-numeric>
                {gefiltert.length}
              </span>
            </button>
            {BUCKET_ORDER.map((bucket) => {
              const anzahl = gruppen.get(bucket)?.length ?? 0;
              if (anzahl === 0 && filter !== bucket) return null;
              return (
                <button
                  key={bucket}
                  type="button"
                  className="chip"
                  data-urgent={bucket === 'ueberfaellig' ? 'true' : undefined}
                  aria-pressed={filter === bucket}
                  onClick={() => setFilter(bucket)}
                >
                  {BUCKET_LABELS[bucket]}
                  <span className="chip__count" data-numeric>
                    {anzahl}
                  </span>
                </button>
              );
            })}
            {ohneAktion > 0 && (
              <button
                type="button"
                className="chip"
                data-counter="true"
                aria-pressed={filter === 'ohne-aktion'}
                onClick={() => setFilter(filter === 'ohne-aktion' ? 'alle' : 'ohne-aktion')}
              >
                Ohne nächste Aktion
                <span className="chip__count" data-numeric>
                  {ohneAktion.toLocaleString('de-CH')}
                </span>
              </button>
            )}
          </div>
        </div>

        {filter === 'ohne-aktion' ? (
          <LiegenGebliebeneListe
            total={ohneAktion}
            items={liegenGeblieben.data?.items ?? []}
            onSelect={waehle}
          />
        ) : items.length === 0 ? (
          <EmptyState title="Keine offene Aufgabe" symbol="✓">
            {ohneAktion > 0
              ? 'Der Stream ist leer — aber oben warten Kontakte, für die noch kein nächster Schritt festgelegt ist.'
              : 'Es ist nichts offen. Neue Aufgaben entstehen, sobald Du einen Vorgang protokollierst oder einen Kontakt einplanst.'}
          </EmptyState>
        ) : gefiltert.length === 0 ? (
          <EmptyState title="Kein Treffer" symbol="⌕">
            Keine Aufgabe passt zu „{entprellt.trim()}". Die Suche läuft über Name, Firma,
            Adresse und Aufgabentitel.
          </EmptyState>
        ) : sichtbareBuendel.length === 0 ? (
          <EmptyState title="In diesem Zeitraum ist nichts offen" symbol="○">
            Wähle „Alle", um die übrigen Aufgaben zu sehen.
          </EmptyState>
        ) : (
          <div className="stream__groups">
            {sichtbareBuendel.map((bucket) => (
              <section key={bucket} className="stream__group">
                <h2 className="stream__group-head" data-urgent={bucket === 'ueberfaellig'}>
                  <span>{BUCKET_LABELS[bucket]}</span>
                  <span className="stream__group-count" data-numeric>
                    {gruppen.get(bucket)?.length}
                  </span>
                </h2>

                <ul className="stream__list">
                  {gruppen.get(bucket)?.map((item) => (
                    <li key={item.contact.id}>
                      <StreamKarte
                        item={item}
                        bucket={bucket}
                        selected={activeContactId === item.contact.id}
                        completing={
                          completeTask.isPending && completeTask.variables === item.task.id
                        }
                        onSelect={() => waehle(item.contact.id)}
                        onComplete={(e) => void handleComplete(item.task.id, item.contact.id, e)}
                        onLog={(e) => {
                          e.stopPropagation();
                          setLogForContactId(
                            logForContactId === item.contact.id ? null : item.contact.id,
                          );
                        }}
                      />

                      {logForContactId === item.contact.id && (
                        <LogForm
                          contactId={item.contact.id}
                          onClose={() => setLogForContactId(null)}
                        />
                      )}

                      {promptNextActionId === item.contact.id && (
                        <NextActionForm
                          contactId={item.contact.id}
                          onClose={() => setPromptNextActionId(null)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>

      {/* --- RECHTS: die Akte & All-in-One Arbeitsplatz ------------------- */}
      <section className="stream__detail" aria-label="Kontakt & Konversation">
        {activeContactId ? (
          <>
            <header className="stream__detail-head">
              <button
                type="button"
                className="back-link"
                onClick={() => setDetailOffen(false)}
              >
                <span aria-hidden="true">←</span> Stream
              </button>
              <h2 className="stream__detail-title">Arbeitsbereich</h2>
              <Link
                to="/kontakte/$id"
                params={{ id: activeContactId }}
                className="btn"
                data-variant="secondary"
              >
                Vollständiges Profil ↗
              </Link>
            </header>

            <div className="stream__detail-body">
              <ActionContactWorkspace contactId={activeContactId} />
            </div>
          </>
        ) : (
          <div className="stream__placeholder">
            <EmptyState title="Kein Kontakt gewählt" symbol="←">
              Wähle links eine Aufgabe oder eine eingegangene Antwort. E-Mails, Verlauf, Notizen und Direkt-Antworten
              erscheinen dann hier.
            </EmptyState>
          </div>
        )}
      </section>
    </div>
  );
}

function anzeigename(kontakt: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}) {
  const voll = `${kontakt.firstName ?? ''} ${kontakt.lastName ?? ''}`.trim();
  return voll.length > 0 ? voll : kontakt.email;
}

/**
 * Eine Aufgabe im Stream.
 *
 * Die Karte selbst waehlt den Kontakt aus; Erledigen und Protokollieren sind
 * eigene Knoepfe darin. Verschachtelte Knoepfe waeren ungueltiges HTML und auf
 * dem Telefon eine Klickfalle — deshalb ist die Karte ein div mit
 * Tastaturbedienung statt eines button.
 */
function StreamKarte({
  item,
  bucket,
  selected,
  completing,
  onSelect,
  onComplete,
  onLog,
}: {
  item: ActionStreamItem;
  bucket: Bucket;
  selected: boolean;
  completing: boolean;
  onSelect: () => void;
  onComplete: (e: React.MouseEvent) => void;
  onLog: (e: React.MouseEvent) => void;
}) {
  const ueberfaellig = bucket === 'ueberfaellig';
  const isReply = item.task.type === 'reply';

  return (
    <article
      className="stream-card"
      data-selected={selected ? 'true' : undefined}
      data-urgent={ueberfaellig ? 'true' : undefined}
      tabIndex={0}
      role="button"
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      style={{
        borderLeft: isReply ? '4px solid #10b981' : undefined,
      }}
    >
      <div className="stream-card__who">
        <strong className="stream-card__name">{anzeigename(item.contact)}</strong>
        {item.contact.companyName && (
          <span className="stream-card__company">{item.contact.companyName}</span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '4px 0' }}>
        {isReply && (
          <span
            className="badge"
            data-tone="success"
            style={{ fontSize: '11px', fontWeight: 600, padding: '2px 6px' }}
          >
            💬 Antwort
          </span>
        )}
        <p className="stream-card__task" style={{ margin: 0, flex: 1, fontWeight: isReply ? 600 : 'normal' }}>
          {item.task.title}
        </p>
      </div>

      <div className="stream-card__foot">
        <span className="stream-card__due" data-urgent={ueberfaellig ? 'true' : undefined}>
          {faelligkeitText(item.task.dueAt)}
        </span>
        <div className="stream-card__actions">
          <Button variant="ghost" onClick={onLog}>
            Protokollieren
          </Button>
          <Button variant="secondary" onClick={onComplete} loading={completing}>
            Erledigen
          </Button>
        </div>
      </div>
    </article>
  );
}

function ActionContactWorkspace({ contactId }: { contactId: string }) {
  const { data: contactData } = useQuery(contactQuery(contactId));
  const { data: threadData, isLoading: threadLoading } = useQuery(contactThreadQuery(contactId));
  const contact = contactData?.contact;
  const messages = useMemo(() => threadData?.messages ?? [], [threadData]);

  const [activeTab, setActiveTab] = useState<'conversation' | 'timeline' | 'notes'>('conversation');
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [inReplyTo, setInReplyTo] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  const sendEmail = useSendContactEmail(contactId);

  const latestInbound = useMemo(
    () => messages.find((m) => m.direction === 'inbound') ?? messages[0],
    [messages],
  );

  const handleOpenReply = (subjectPrefix?: string, replyToMsgId?: string) => {
    let sub = replySubject;
    if (!sub || subjectPrefix) {
      const baseSub = subjectPrefix ?? latestInbound?.subject ?? 'Ihre Anfrage';
      sub = baseSub.toLowerCase().startsWith('re:') ? baseSub : `Re: ${baseSub}`;
    }
    setReplySubject(sub);
    setInReplyTo(replyToMsgId ?? latestInbound?.messageId ?? null);
    setShowReplyBox(true);
    setSendSuccess(false);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replySubject.trim() || !replyBody.trim()) return;

    await sendEmail.mutateAsync({
      subject: replySubject.trim(),
      textBody: replyBody.trim(),
      inReplyTo,
    });

    setReplyBody('');
    setShowReplyBox(false);
    setSendSuccess(true);
    setTimeout(() => setSendSuccess(false), 5000);
  };

  const customFields = (contact?.custom ?? {}) as Record<string, any>;
  const googleUrl = (customFields.google_profile_url || customFields.google_url) as string | undefined;

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      {/* 1. Header Card mit Kontakt-Infos & Quick Actions */}
      <section className="card" style={{ background: 'var(--color-surface)' }}>
        <div className="card__body stack" style={{ gap: 'var(--space-3)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 'var(--space-3)',
            }}
          >
            <div>
              <h2 style={{ fontSize: 'var(--text-xl)', margin: 0, fontWeight: 700 }}>
                {contact
                  ? contact.firstName || contact.lastName
                    ? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()
                    : contact.email
                  : 'Lade Kontakt...'}
              </h2>
              {contact && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginTop: 4,
                    flexWrap: 'wrap',
                  }}
                >
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-sm muted"
                    style={{ textDecoration: 'underline' }}
                  >
                    ✉️ {contact.email}
                  </a>
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone}`}
                      className="text-sm muted"
                      style={{ textDecoration: 'underline' }}
                    >
                      📞 {contact.phone}
                    </a>
                  )}
                  {contact.city && (
                    <span className="text-sm muted">
                      📍 {contact.city}
                      {contact.country ? `, ${contact.country}` : ''}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {googleUrl && (
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--sm"
                  data-variant="secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  📍 Google Maps Profil ↗
                </a>
              )}
              <Link
                to="/kontakte/$id"
                params={{ id: contactId }}
                className="btn btn--sm"
                data-variant="ghost"
              >
                👤 Stammdaten
              </Link>
            </div>
          </div>

          {contact && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
                alignItems: 'center',
                borderTop: '1px solid var(--color-border)',
                paddingTop: 'var(--space-2)',
              }}
            >
              <span className="badge" data-tone="neutral">
                Phase: {contact.lifecycleStage}
              </span>
              <span
                className="badge"
                data-tone={contact.leadScore > 50 ? 'success' : 'neutral'}
              >
                ⭐ Score: {contact.leadScore}
              </span>
              <span
                className="badge"
                data-tone={contact.status === 'active' ? 'success' : 'warning'}
              >
                Status: {contact.status}
              </span>
              {messages.length > 0 && (
                <span className="badge" data-tone="accent">
                  💬 {messages.length} Nachricht{messages.length === 1 ? '' : 'en'}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 2. Workspace Tabs: Konversation (E-Mails), Zeitstrahl, Notiz */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: 'var(--space-2)',
        }}
      >
        <button
          type="button"
          className="chip"
          aria-pressed={activeTab === 'conversation'}
          onClick={() => setActiveTab('conversation')}
        >
          💬 E-Mail-Konversation ({messages.length})
        </button>
        <button
          type="button"
          className="chip"
          aria-pressed={activeTab === 'timeline'}
          onClick={() => setActiveTab('timeline')}
        >
          📜 Zeitstrahl & Verlauf
        </button>
        <button
          type="button"
          className="chip"
          aria-pressed={activeTab === 'notes'}
          onClick={() => setActiveTab('notes')}
        >
          📝 Notiz hinzufügen
        </button>
      </div>

      {sendSuccess && (
        <div className="alert" data-tone="success" role="status">
          ✅ E-Mail-Antwort wurde erfolgreich versendet und im Verlauf archiviert!
        </div>
      )}

      {/* 3. Tab: E-Mail-Konversation (Inbound & Outbound direkt bearbeitbar) */}
      {activeTab === 'conversation' && (
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          {/* Quick-Antwort-Button & Formular */}
          {!showReplyBox ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--color-surface)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div>
                <strong>Direkt per E-Mail antworten</strong>
                <p className="text-xs muted" style={{ margin: 0 }}>
                  Antworte direkt aus dem Action Stream — ohne in den Posteingang zu wechseln.
                </p>
              </div>
              <Button variant="primary" onClick={() => handleOpenReply()}>
                ✉️ E-Mail verfassen / Antworten
              </Button>
            </div>
          ) : (
            <section
              className="card"
              style={{ border: '2px solid var(--color-accent)' }}
            >
              <div className="card__body stack" style={{ gap: 'var(--space-3)' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h3
                    style={{
                      fontSize: 'var(--text-base)',
                      margin: 0,
                      fontWeight: 600,
                    }}
                  >
                    ✉️ Antwort an {contact?.email}
                  </h3>
                  <Button variant="ghost" onClick={() => setShowReplyBox(false)}>
                    ✕ Schließen
                  </Button>
                </div>

                {sendEmail.isError && (
                  <Alert tone="danger" title="Versand fehlgeschlagen">
                    {sendEmail.error instanceof Error
                      ? sendEmail.error.message
                      : 'Fehler beim Senden.'}
                  </Alert>
                )}

                <form onSubmit={handleSend} className="stack" style={{ gap: 'var(--space-3)' }}>
                  <div>
                    <label
                      className="text-xs muted"
                      style={{ display: 'block', marginBottom: 2 }}
                    >
                      Betreff
                    </label>
                    <input
                      className="input"
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      placeholder="Betreff ..."
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="text-xs muted"
                      style={{ display: 'block', marginBottom: 2 }}
                    >
                      Nachricht
                    </label>
                    <textarea
                      className="input"
                      rows={6}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder="Guten Tag ...&#10;&#10;Vielen Dank für Ihre Rückmeldung ..."
                      style={{ resize: 'vertical', width: '100%' }}
                      required
                      autoFocus
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setShowReplyBox(false)}
                    >
                      Abbrechen
                    </Button>
                    <Button variant="primary" type="submit" loading={sendEmail.isPending}>
                      ✉️ Antwort jetzt absenden
                    </Button>
                  </div>
                </form>
              </div>
            </section>
          )}

          {/* Liste aller Nachrichten */}
          {threadLoading ? (
            <Skeleton lines={4} />
          ) : messages.length === 0 ? (
            <EmptyState title="Keine E-Mails vorhanden" symbol="✉">
              Für diesen Kontakt sind noch keine ein- oder ausgehenden E-Mails im Posteingang
              eingetroffen. Klicke oben auf „E-Mail verfassen", um die erste Nachricht zu senden.
            </EmptyState>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              {messages.map((msg) => {
                const isInbound = msg.direction === 'inbound';
                return (
                  <article
                    key={msg.id}
                    className="card"
                    style={{
                      borderLeft: isInbound
                        ? '4px solid #10b981'
                        : '4px solid var(--color-accent)',
                      background: isInbound
                        ? 'var(--color-surface)'
                        : 'var(--color-surface-hover)',
                    }}
                  >
                    <div className="card__body stack" style={{ gap: 'var(--space-2)' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 'var(--space-2)',
                        }}
                      >
                        <div>
                          <span
                            className="badge"
                            data-tone={isInbound ? 'success' : 'accent'}
                            style={{ marginRight: 6, fontSize: '11px' }}
                          >
                            {isInbound ? '📩 Eingehend von Lead' : '📤 Ausgehend gesendet'}
                          </span>
                          <strong>{msg.fromEmail}</strong>
                          {msg.toEmails?.length > 0 && (
                            <span className="text-xs muted" style={{ marginLeft: 6 }}>
                              an {msg.toEmails.join(', ')}
                            </span>
                          )}
                        </div>
                        <span className="text-xs muted">
                          {new Date(msg.receivedAt).toLocaleString('de-CH')}
                        </span>
                      </div>

                      {msg.subject && (
                        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                          {msg.subject}
                        </div>
                      )}

                      <div
                        style={{
                          fontSize: 'var(--text-sm)',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.5,
                          background: 'rgba(0,0,0,0.02)',
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {msg.textBody || '(Kein Textinhalt)'}
                      </div>

                      {isInbound && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: 'var(--space-1)',
                          }}
                        >
                          <Button
                            variant="ghost"
                            onClick={() =>
                              handleOpenReply(msg.subject ?? undefined, msg.messageId)
                            }
                            style={{ fontSize: 'var(--text-xs)' }}
                          >
                            💬 Auf diese Nachricht antworten
                          </Button>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 4. Tab: CRM Zeitstrahl */}
      {activeTab === 'timeline' && <ContactTimeline contactId={contactId} />}

      {/* 5. Tab: Notiz hinzufügen */}
      {activeTab === 'notes' && (
        <section className="card">
          <div className="card__body stack" style={{ gap: 'var(--space-3)' }}>
            <h3 style={{ fontSize: 'var(--text-base)', margin: 0 }}>Notiz erfassen</h3>
            <AddContactNoteForm contactId={contactId} />
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Der Gegenzaehler als eigene Liste.
 *
 * Sortiert nach Lead-Score: wer als Naechstes einen Termin bekommen sollte,
 * steht oben. Eine alphabetische Liste waere hier wertlos.
 */
function LiegenGebliebeneListe({
  total,
  items,
  onSelect,
}: {
  total: number;
  items: ContactWithoutNextAction[];
  onSelect: (contactId: string) => void;
}) {
  if (items.length === 0) {
    return (
      <EmptyState title="Jeder Kontakt hat einen nächsten Schritt" symbol="✓">
        Es liegt niemand ohne Folgeaufgabe herum. Das ist der Zustand, den diese Liste anstrebt.
      </EmptyState>
    );
  }

  return (
    <div className="stack" data-gap="3">
      <Alert tone="warning" title="Ohne nächsten Schritt">
        <strong data-numeric>{total.toLocaleString('de-CH')}</strong> Kontakte haben keine offene
        Aufgabe. Unten stehen die {items.length} mit dem höchsten Lead-Score — der Rest folgt,
        sobald diese eingeplant sind.
      </Alert>

      <ul className="stream__list">
        {items.map((kontakt) => (
          <li key={kontakt.id}>
            <LiegengebliebenerKontakt kontakt={kontakt} onSelect={() => onSelect(kontakt.id)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function LiegengebliebenerKontakt({
  kontakt,
  onSelect,
}: {
  kontakt: ContactWithoutNextAction;
  onSelect: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const nameIstAdresse = anzeigename(kontakt) === kontakt.email;

  return (
    <article
      className="stream-card"
      tabIndex={0}
      role="button"
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="stream-card__who">
        <strong className="stream-card__name">{anzeigename(kontakt)}</strong>
        <span className="stream-card__company">
          {/* Ohne Namen ist die Ueberschrift schon die Adresse — dann steht
              sie hier nicht ein zweites Mal. */}
          {[kontakt.companyName, nameIstAdresse ? null : kontakt.email]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>

      <div className="stream-card__foot">
        <Badge tone={kontakt.leadScore >= 50 ? 'warning' : 'neutral'}>
          Score {kontakt.leadScore}
        </Badge>
        <div className="stream-card__actions">
          <Button
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setOffen((o) => !o);
            }}
          >
            Nächste Aktion
          </Button>
        </div>
      </div>

      {offen && <NextActionForm contactId={kontakt.id} onClose={() => setOffen(false)} />}
    </article>
  );
}

/**
 * Festhalten, was ausserhalb des Systems passiert ist.
 *
 * Die Folgeaufgabe steht bewusst im selben Formular: der beste Zeitpunkt, den
 * naechsten Schritt festzulegen, ist unmittelbar nach dem Gespraech. Wer erst
 * speichert und dann ein zweites Formular sucht, laesst es meistens bleiben.
 */
function LogForm({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const log = useLogActivity();
  const [kind, setKind] = useState<'call' | 'meeting' | 'email' | 'note'>('call');
  const [note, setNote] = useState('');
  const [nextTitle, setNextTitle] = useState('');
  const [nextDue, setNextDue] = useState('');

  async function speichern(event: React.FormEvent) {
    event.preventDefault();
    await log.mutateAsync({
      contactId,
      kind,
      note: note.trim() || null,
      nextAction: nextTitle.trim()
        ? {
            title: nextTitle.trim(),
            type: 'todo',
            dueAt: nextDue ? new Date(nextDue).toISOString() : null,
          }
        : null,
    });
    onClose();
  }

  return (
    <div className="stream-form" onClick={(e) => e.stopPropagation()}>
      <h3 className="stream-form__title">Was ist passiert?</h3>

      {log.isError && (
        <Alert tone="danger" title="Nicht gespeichert">
          {log.error instanceof Error ? log.error.message : 'Unbekannter Fehler.'}
        </Alert>
      )}

      <form className="stream-form__grid" onSubmit={speichern}>
        <select
          className="input"
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          aria-label="Art des Vorgangs"
        >
          <option value="call">Anruf</option>
          <option value="meeting">Termin</option>
          <option value="email">E-Mail gesendet</option>
          <option value="note">Notiz</option>
        </select>
        <input
          className="input"
          value={note}
          placeholder="Ergebnis in einem Satz"
          aria-label="Ergebnis"
          onChange={(e) => setNote(e.target.value)}
        />
        <input
          className="input"
          value={nextTitle}
          placeholder="Nächste Aktion (optional) — z. B. in 2 Wochen nachfassen"
          aria-label="Nächste Aktion"
          onChange={(e) => setNextTitle(e.target.value)}
        />
        <input
          className="input"
          type="datetime-local"
          value={nextDue}
          aria-label="Nächste Aktion fällig am"
          onChange={(e) => setNextDue(e.target.value)}
        />
        <div className="stream-form__actions">
          <Button type="submit" loading={log.isPending}>
            Speichern
          </Button>
          <Button variant="ghost" type="button" onClick={onClose}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}

function NextActionForm({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [type, setType] = useState('todo');

  const create = useCreateTask();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    await create.mutateAsync({
      title: title.trim(),
      type,
      priority: 'normal',
      dueAt: due ? new Date(due).toISOString() : null,
      contactId,
    });
    onClose();
  };

  return (
    <div className="stream-form" data-accent="true" onClick={(e) => e.stopPropagation()}>
      <h3 className="stream-form__title">Nächste Aktion festlegen</h3>
      <p className="text-sm muted">
        Erledigt ist erst, wenn feststeht, was danach kommt — sonst fällt der Kontakt aus dem
        Stream und taucht nie wieder auf.
      </p>

      <form className="stream-form__grid" onSubmit={handleSubmit}>
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Anrufen, Nachfassen …"
          aria-label="Titel der nächsten Aktion"
          autoFocus
        />
        <select
          className="input"
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Art der Aufgabe"
        >
          <option value="call">Anruf</option>
          <option value="email">E-Mail</option>
          <option value="meeting">Termin</option>
          <option value="todo">Aufgabe</option>
        </select>
        <input
          className="input"
          type="datetime-local"
          value={due}
          aria-label="Fällig am"
          onChange={(e) => setDue(e.target.value)}
        />
        <div className="stream-form__actions">
          <Button type="submit" loading={create.isPending}>
            Speichern
          </Button>
          <Button variant="ghost" type="button" onClick={onClose}>
            Überspringen
          </Button>
        </div>
      </form>
    </div>
  );
}
